import { Box, Chip, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Table, TableBody, TableCell, TableHead, TableRow, TextField, Tooltip, Typography } from '@mui/material';
import { Campaign as CampaignIcon, Send as SendIcon, Delete as DeleteIcon, Visibility as VisibilityIcon, Close as CloseIcon } from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import * as Yup from 'yup';
import { IPropsBusinessPage } from '../Business';
import { campaignService, clientService, feedback, ICampaignView, ICampaignDetailView } from '../../../shared/services';
import { Button } from '../../../shared/components/MUI/button/Button';
import { FilterBuilder, FilterChips } from '../../../shared/components/SconApp/filterBuilder/FilterBuilder';
import { FilterField, FilterRow, FilterState } from '../../../shared/components/SconApp/filterBuilder/filterTypes';
import { DeleteConfirmationDialog } from '../../../shared/components';

const clientFilterFields: FilterField[] = [
  { key: 'lastVisit', label: 'Última visita', operators: [{ value: 'gt', label: 'há mais de' }, { value: 'lt', label: 'há menos de' }], valueType: 'number', suffix: 'dias' },
  { key: 'totalAppointments', label: 'Atendimentos', operators: [{ value: 'gte', label: '≥' }, { value: 'lte', label: '≤' }], valueType: 'number', suffix: 'vezes' },
  { key: 'whatsAppConfirmed', label: 'WhatsApp confirmado', operators: [{ value: 'eq', label: 'é' }], valueType: 'boolean', booleanOptions: [{ label: 'Sim', value: true }, { label: 'Não', value: false }] },
];

const schema = Yup.object().shape({
  title: Yup.string().trim().min(3, 'Mínimo de 3 caracteres').max(200, 'Máximo de 200 caracteres').required('Título é obrigatório'),
  message: Yup.string().trim().min(10, 'Mínimo de 10 caracteres').max(1500, 'Máximo de 1500 caracteres').required('Mensagem é obrigatória'),
});

const statusColor: Record<string, string> = {
  'Rascunho': 'default',
  'Enviando': 'warning',
  'Enviado': 'success',
  'Cancelado': 'error',
};

function buildFilterParams(rows: FilterRow[]): Record<string, string | number | boolean | undefined> {
  const params: Record<string, string | number | boolean | undefined> = {};
  for (const row of rows) {
    if (row.value === '' || row.value === undefined) continue;
    if (row.field === 'lastVisit') {
      if (row.operator === 'gt') params.lastVisitBeforeDays = Number(row.value);
      if (row.operator === 'lt') params.lastVisitAfterDays = Number(row.value);
    } else if (row.field === 'totalAppointments') {
      if (row.operator === 'gte') params.minCompletedAppointments = Number(row.value);
      if (row.operator === 'lte') params.maxCompletedAppointments = Number(row.value);
    } else if (row.field === 'whatsAppConfirmed') {
      params.whatsAppConfirmed = row.value === true || row.value === 'true';
    }
  }
  return params;
}

export const BusinessCampaigns: React.FC<IPropsBusinessPage> = ({ business }) => {
  const queryClient = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);
  const [detailCampaign, setDetailCampaign] = useState<ICampaignDetailView | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<ICampaignView | null>(null);
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [selectedClientIds, setSelectedClientIds] = useState<number[]>([]);
  const [filterRows, setFilterRows] = useState<FilterRow[]>([]);
  const [error, setError] = useState('');

  // Fetch campaigns
  const { data: campaigns, isFetching } = useQuery({
    queryKey: ['campaigns', business.id],
    queryFn: () => campaignService.getAll(business.id),
  });

  // Fetch filtered clients for preview
  const filterParams = buildFilterParams(filterRows);
  const hasFilters = filterRows.length > 0;
  const { data: pagedClients } = useQuery({
    queryKey: ['campaign-preview', business.id, filterParams],
    queryFn: () => {
      return clientService.getClients(business.id, 1, 500, undefined, {
        lastVisitBeforeDays: filterParams.lastVisitBeforeDays as number | undefined,
        lastVisitAfterDays: filterParams.lastVisitAfterDays as number | undefined,
        minCompletedAppointments: filterParams.minCompletedAppointments as number | undefined,
        maxCompletedAppointments: filterParams.maxCompletedAppointments as number | undefined,
        whatsAppConfirmed: filterParams.whatsAppConfirmed as boolean | undefined,
      });
    },
    enabled: createOpen && hasFilters,
    keepPreviousData: true,
  });

  // Create campaign
  const createMutation = useMutation({
    mutationFn: () => campaignService.create(business.id, {
      title: title.trim(),
      message: message.trim(),
      clientIds: selectedClientIds,
    }),
    onSuccess: () => {
      queryClient.invalidateQueries(['campaigns', business.id]);
      handleCloseCreate();
      feedback('Campanha criada com sucesso!', 'success');
    },
    onError: (err: any) => {
      feedback(err?.response?.data?.error || 'Erro ao criar campanha', 'error');
    },
  });

  // Send campaign
  const sendMutation = useMutation({
    mutationFn: (campaignId: number) => campaignService.send(business.id, campaignId),
    onSuccess: () => {
      queryClient.invalidateQueries(['campaigns', business.id]);
      feedback('Envio iniciado! As mensagens serão enviadas gradualmente.', 'success');
    },
    onError: (err: any) => {
      feedback(err?.response?.data?.error || 'Erro ao iniciar envio', 'error');
    },
  });

  // Delete/cancel campaign
  const deleteMutation = useMutation({
    mutationFn: (campaignId: number) => campaignService.cancel(business.id, campaignId),
    onSuccess: () => {
      queryClient.invalidateQueries(['campaigns', business.id]);
      setDeleteTarget(null);
      feedback('Campanha cancelada.', 'success');
    },
    onError: (err: any) => {
      feedback(err?.response?.data?.error || 'Erro ao cancelar', 'error');
    },
  });

  const handleOpenCreate = () => {
    setTitle('');
    setMessage('');
    setSelectedClientIds([]);
    setFilterRows([]);
    setError('');
    setCreateOpen(true);
  };

  const handleCloseCreate = () => {
    setCreateOpen(false);
    setTitle('');
    setMessage('');
    setSelectedClientIds([]);
    setFilterRows([]);
    setError('');
  };

  const handleFilterApply = useCallback((state: FilterState) => {
    setFilterRows(state.rows);
    // Update selected clients based on filter results (will be fetched by query)
  }, []);

  const handleFilterClear = useCallback(() => {
    setFilterRows([]);
    setSelectedClientIds([]);
  }, []);

  // When pagedClients changes due to filter, auto-select all
  const handleSelectFiltered = () => {
    const clients = pagedClients?.items ?? [];
    setSelectedClientIds(clients.map(c => c.clientId));
  };

  const handleCreate = async () => {
    setError('');
    try {
      await schema.validate({ title, message }, { abortEarly: false });
    } catch (err: any) {
      if (err.inner?.length) {
        setError(err.inner.map((e: any) => e.message).join('. '));
        return;
      }
    }

    if (selectedClientIds.length === 0) {
      setError('Selecione pelo menos um cliente aplicando os filtros.');
      return;
    }

    if (selectedClientIds.length > 50) {
      setError('Máximo de 50 clientes por campanha.');
      return;
    }

    createMutation.mutate();
  };

  const handleViewDetail = async (campaign: ICampaignView) => {
    try {
      const detail = await campaignService.getById(business.id, campaign.id);
      setDetailCampaign(detail);
    } catch {
      feedback('Erro ao carregar detalhes', 'error');
    }
  };

  return (
    <Box display='flex' flexDirection='column' gap={2}>
      {/* Header */}
      <Box display='flex' justifyContent='space-between' alignItems='center'>
        <Typography variant='h6'>Campanhas</Typography>
        <Button
          variant='contained'
          startIcon={<CampaignIcon />}
          onClick={handleOpenCreate}
          size='small'
        >
          Nova Campanha
        </Button>
      </Box>

      {/* Campaigns table */}
      <Table size='small'>
        <TableHead>
          <TableRow>
            <TableCell>Título</TableCell>
            <TableCell>Status</TableCell>
            <TableCell align='center'>Destinatários</TableCell>
            <TableCell align='center'>Enviados</TableCell>
            <TableCell>Criada em</TableCell>
            <TableCell align='center'>Ações</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {!campaigns?.length && !isFetching && (
            <TableRow>
              <TableCell colSpan={6} align='center'>
                <Typography color='text.secondary' sx={{ py: 4 }}>
                  Nenhuma campanha ainda. Clique em &quot;Nova Campanha&quot; para começar.
                </Typography>
              </TableCell>
            </TableRow>
          )}
          {campaigns?.map(c => (
            <TableRow key={c.id}>
              <TableCell>{c.title}</TableCell>
              <TableCell>
                <Chip
                  label={c.statusLabel}
                  color={(statusColor[c.statusLabel] as any) ?? 'default'}
                  size='small'
                />
              </TableCell>
              <TableCell align='center'>{c.recipientCount}</TableCell>
              <TableCell align='center'>{c.sentCount}</TableCell>
              <TableCell>{new Date(c.createdAt).toLocaleDateString('pt-BR')}</TableCell>
              <TableCell align='center'>
                <Box display='flex' gap={0.5} justifyContent='center'>
                  <Tooltip title='Ver detalhes'>
                    <IconButton size='small' onClick={() => handleViewDetail(c)}>
                      <VisibilityIcon fontSize='small' />
                    </IconButton>
                  </Tooltip>
                  {c.status === 0 && (
                    <>
                      <Tooltip title='Enviar'>
                        <IconButton
                          size='small'
                          color='primary'
                          onClick={() => sendMutation.mutate(c.id)}
                        >
                          <SendIcon fontSize='small' />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title='Cancelar'>
                        <IconButton
                          size='small'
                          color='error'
                          onClick={() => setDeleteTarget(c)}
                        >
                          <DeleteIcon fontSize='small' />
                        </IconButton>
                      </Tooltip>
                    </>
                  )}
                </Box>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {/* ───── Create Campaign Dialog ───── */}
      <Dialog open={createOpen} onClose={handleCloseCreate} maxWidth='sm' fullWidth>
        <DialogTitle>
          Nova Campanha
          <IconButton onClick={handleCloseCreate} sx={{ position: 'absolute', right: 8, top: 8 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box display='flex' flexDirection='column' gap={2} sx={{ mt: 1 }}>
            {error && <Typography color='error' variant='body2'>{error}</Typography>}

            <TextField
              label='Título da campanha'
              value={title}
              onChange={e => setTitle(e.target.value)}
              fullWidth
              size='small'
              placeholder='Ex: Reativação de clientes'
            />

            <TextField
              label='Mensagem'
              value={message}
              onChange={e => setMessage(e.target.value)}
              fullWidth
              multiline
              minRows={4}
              maxRows={8}
              size='small'
              placeholder='Olá {{nome}}, saudades! Passa lá no salão...'
              helperText={`${message.length}/1500 caracteres. Use {{nome}} para personalizar.`}
            />

            <Typography variant='subtitle2' sx={{ mt: 1 }}>Selecionar clientes</Typography>

            <FilterBuilder
              fields={clientFilterFields}
              onApply={handleFilterApply}
              onClear={handleFilterClear}
              activeFilters={filterRows.length}
            />

            {filterRows.length > 0 && (
              <>
                <FilterChips
                  rows={filterRows}
                  fields={clientFilterFields}
                  onRemove={(id) => setFilterRows(prev => prev.filter(r => r.id !== id))}
                  onClear={handleFilterClear}
                />

                <Button
                  variant='outlined'
                  size='small'
                  onClick={handleSelectFiltered}
                  disabled={!pagedClients}
                >
                  Selecionar {pagedClients?.items?.length ?? 0} cliente(s) encontrados
                </Button>
              </>
            )}

            {selectedClientIds.length > 0 && (
              <Box>
                <Typography variant='caption' color='text.secondary'>
                  {selectedClientIds.length} cliente(s) selecionado(s) (máx 50)
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCreate} color='inherit'>Cancelar</Button>
          <Button
            variant='contained'
            onClick={handleCreate}
            isLoading={createMutation.isLoading}
          >
            Criar Campanha
          </Button>
        </DialogActions>
      </Dialog>

      {/* ───── Detail Dialog ───── */}
      <Dialog open={!!detailCampaign} onClose={() => setDetailCampaign(null)} maxWidth='md' fullWidth>
        <DialogTitle>
          {detailCampaign?.title}
          <IconButton onClick={() => setDetailCampaign(null)} sx={{ position: 'absolute', right: 8, top: 8 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {detailCampaign && (
            <Box display='flex' flexDirection='column' gap={2} sx={{ mt: 1 }}>
              <Typography variant='body2' sx={{ whiteSpace: 'pre-wrap', bgcolor: 'grey.100', p: 2, borderRadius: 1 }}>
                {detailCampaign.message}
              </Typography>

              <Table size='small'>
                <TableHead>
                  <TableRow>
                    <TableCell>Cliente</TableCell>
                    <TableCell>Telefone</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Enviado em</TableCell>
                    <TableCell>Erro</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {detailCampaign.recipients.map(r => (
                    <TableRow key={r.id}>
                      <TableCell>{r.clientName}</TableCell>
                      <TableCell>{r.phone}</TableCell>
                      <TableCell>
                        <Chip
                          label={r.statusLabel}
                          size='small'
                          color={r.status === 1 ? 'success' : r.status === 2 ? 'error' : 'default'}
                        />
                      </TableCell>
                      <TableCell>{r.sentAt ? new Date(r.sentAt).toLocaleString('pt-BR') : '—'}</TableCell>
                      <TableCell>{r.errorMessage || '—'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailCampaign(null)}>Fechar</Button>
        </DialogActions>
      </Dialog>

      {/* ───── Delete Confirmation ───── */}
      <DeleteConfirmationDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
        isLoading={deleteMutation.isLoading}
        label='Tem certeza que deseja cancelar esta campanha? Só é possível cancelar campanhas em rascunho.'
      />
    </Box>
  );
};
